import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Flex, Text, Title, Button, Box } from '@mantine/core';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { getExtraIdentifierRows } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
}

export const InvestmentLeaveCompleted = ({ setIsContinueDisabled }: Props) => {
	const tInvestor = useTranslationInvestor();
	const [showModal, setShowModal] = useState(true);
	const getInvestorDataRequest = useInvestor();
	const investorBankAccountDetails = getInvestorDataRequest.data?.person?.bankAccounts?.[0];
	const identifierRows = getExtraIdentifierRows(investorBankAccountDetails?.identifiers);
	const countryName = investorBankAccountDetails?.countryCode
		? getCountryNameByCode(investorBankAccountDetails.countryCode)
		: '';

	useEffect(() => {
		setIsContinueDisabled(false);
	}, []);

	return (
		<Box>
			<Flex
				direction="column"
				gap="2rem"
				bg="blue.0"
				mt="2rem"
				p="2rem"
				w="100%"
				sx={{
					borderRadius: '4px',
					position: 'relative',
				}}
			>
				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							1
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.leave.completed.step_1.title')}
						</Title>
						<Text sx={{ maxWidth: '40rem' }}>
							{tInvestor('investment.leave.completed.step_1.subtitle')}
						</Text>
					</Flex>
				</Flex>

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							2
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.leave.completed.step_2.title')}
						</Title>
						<Text sx={{ maxWidth: '20rem' }}>
							{tInvestor('investment.leave.completed.step_2.subtitle')}
						</Text>
						{!!investorBankAccountDetails && (
							<ListWrapper className={REPLAY_MASK_CLASS} data-sentry-mask>
								<GreyCard
									title={tInvestor('dashboard.project_modal.account_holder')}
									text={investorBankAccountDetails.accountHolder}
								/>
								<GreyCard
									title={tInvestor('dashboard.project_modal.bank_name')}
									text={investorBankAccountDetails.bankName}
								/>
								{investorBankAccountDetails.accountNumber ? (
									<GreyCard
										title={tInvestor('dashboard.project_modal.account_number')}
										text={investorBankAccountDetails.accountNumber}
									/>
								) : null}
								{investorBankAccountDetails.iban ? (
									<GreyCard
										title={tInvestor('dashboard.project_modal.iban')}
										text={investorBankAccountDetails.iban}
									/>
								) : null}
								{investorBankAccountDetails.swiftCode ? (
									<GreyCard
										title={tInvestor('dashboard.project_modal.swift_code')}
										text={investorBankAccountDetails.swiftCode}
									/>
								) : null}
								{identifierRows.map((row) => (
									<GreyCard key={row.key} title={row.label} text={row.value} />
								))}
								{countryName ? (
									<GreyCard
										title={tInvestor('dashboard.project_modal.country', {
											defaultValue: 'Country',
										})}
										text={countryName}
									/>
								) : null}
								{investorBankAccountDetails.currency ? (
									<GreyCard
										title={tInvestor('dashboard.project_modal.currency', {
											defaultValue: 'Currency',
										})}
										text={investorBankAccountDetails.currency}
									/>
								) : null}
							</ListWrapper>
						)}
					</Flex>
				</Flex>

				<Flex align="baseline" gap="0.5rem">
					<Circle variant="filled">
						<Text fz="lg" fw={600} lh="1.43rem" c="gray.0">
							3
						</Text>
					</Circle>
					<Flex direction="column" gap="0.5rem">
						<Title order={5}>
							{tInvestor('investment.leave.completed.step_3.title')}
						</Title>
					</Flex>
				</Flex>
			</Flex>

			{showModal && (
				<ConfirmationModalV3
					title={tInvestor('investment.continue_confirm_modal.title')}
					description={tInvestor('investment.continue_confirm_modal.subtitle')}
					children={
						<Flex>
							<Button
								size="md"
								variant="secondary"
								onClick={() => setShowModal(false)}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_CONFIRM
								}
							>
								{tInvestor('investment.join.completed.modal.understood')}
							</Button>
						</Flex>
					}
				/>
			)}
		</Box>
	);
};

const ListWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
	grid-auto-rows: 1fr;
	grid-column-gap: 0.5rem;
`;

const GreyCard = styled(
	({ title, text, className }: { title: string; text?: string | null; className?: string }) => {
		return (
			<div className={className}>
				<Text fz="sm" fw={600}>
					{title}
				</Text>
				<Text sx={{ wordBreak: 'break-all' }}>{text || LONG_DASH}</Text>
			</div>
		);
	}
)`
	width: 8.75rem;
	padding: 0.5rem;
	background-color: ${(props) => props.theme.v3.colors.gray[0]};
	border: 1px solid ${(props) => props.theme.v3.colors.gray[4]};
`;
